import { Enums } from '@bode-canada/core';
import {
  OffersManagementListingDtoField,
  OffersManagementOfferDtoField,
  OffersManagementUserDtoField,
} from '@/services/api/offers-management/enums';
import DateHelper from '@/ui-kit/helpers/DateHelper';
import OffersManagementFilterService from './OffersManagementFilterService';
import { UserRole } from '@/constants/user';
import { OfferStatus } from '@/constants/offer';
import {
  INITIAL_FOR_OPPONENT_STEPPER_STATUSES,
  PREPARING_STEPPER_STATUSES,
} from '../stepper/constants';
import {
  BuyerCommissionName,
  CommissionType,
} from '@/components/page-specific/offers/buyer-commission/enums';
import { ThresholdPrice } from '@/utils/commission/constants';
import CommissionHelper from '@/utils/commission';

class OfferManagementOfferDetailsService {
  static getBuyerAgentCommissionTerm(offer) {
    const { listingPrice, listingProvince } = offer;
    const buyerTerms = JSON.parse(offer[OffersManagementOfferDtoField.OfferBuyerTerms]);
    let commissionTerm = '';

    if (buyerTerms) {
      commissionTerm = buyerTerms
        .filter((term) => term)
        .find((term) => term.name === BuyerCommissionName && term.checked && !term.removed);
    }

    if (commissionTerm) {
      return this.calculateBuyerAgentCommission(commissionTerm, listingPrice, listingProvince);
    }

    return null;
  }

  static calculateBuyerAgentCommission(term, price, province) {
    switch (term.commissionType) {
      case CommissionType.FlatRate:
        return term.value;
      case CommissionType.Typical:
        return CommissionHelper.getBuyerCommission(price, province);
      case CommissionType.Custom:
        return CommissionHelper
          .getCommissionByRate(this.#getCustomCommissionRates(term.value), price);
      default:
        return 0;
    }
  }

  static #getCustomCommissionRates(termValue) {
    const {
      firstPartOfSalePriceCommission,
      balanceSalePriceCommission,
    } = termValue;

    return [
      { value: firstPartOfSalePriceCommission, thresholdPrice: ThresholdPrice.Min },
      { value: balanceSalePriceCommission, thresholdPrice: ThresholdPrice.Max },
    ];
  }

  static getNumberOfConditions(offer) {
    const sellerConditions = offer[OffersManagementOfferDtoField.OfferSellerConditions];
    const buyerConditions = offer[OffersManagementOfferDtoField.OfferBuyerConditions];

    const numberOfSellerConditions = Object.keys(sellerConditions).length;
    const numberOfBuyerConditions = Object.keys(buyerConditions).length;

    return numberOfSellerConditions + numberOfBuyerConditions;
  }

  static getIsSomeConditionNotSatisfied(offers) {
    return offers.some((offer) => (
      offer[OffersManagementOfferDtoField.OfferAreConditionsNotSatisfied]
    ));
  }

  static getIsPossessionDayComplete(offer) {
    return DateHelper.getIsPast(offer[OffersManagementOfferDtoField.OfferPossessionDate]);
  }

  static getIsPossessionDatePassed(offers) {
    const lastOffer = OffersManagementFilterService.getLastOffer(offers);
    const possessionDate = new Date(lastOffer[OffersManagementOfferDtoField.OfferPossessionDate]);
    const today = new Date();

    DateHelper.resetDateHours(possessionDate);
    DateHelper.resetDateHours(today);

    return today > new Date(possessionDate);
  }

  static getHasCounterOffers(offers) {
    const pendingOffers = this.getPendingOffers(offers);
    let hasBuyerPendingOffer = false;
    let hasSellerPendingOffer = false;

    pendingOffers.forEach((offer) => {
      const isBuyerOffer = offer[OffersManagementUserDtoField.UserRole] === UserRole.Buyer;
      const isSellerOffer = offer[OffersManagementUserDtoField.UserRole] === UserRole.Seller;

      hasBuyerPendingOffer = hasBuyerPendingOffer || isBuyerOffer;
      hasSellerPendingOffer = hasSellerPendingOffer || isSellerOffer;
    });

    return hasBuyerPendingOffer && hasSellerPendingOffer;
  }

  static getLastAcceptedOffer(offers) {
    const sortedOffers = OffersManagementFilterService.getDescSortedOffersByDate(offers);

    return sortedOffers.find((offer) => (
      offer[OffersManagementOfferDtoField.OfferStatus] === OfferStatus.Accepted
    ));
  }

  static getIsAccepted(offers) {
    const lastAcceptedOffer = this.getLastAcceptedOffer(offers);

    if (!lastAcceptedOffer) {
      return false;
    }

    return lastAcceptedOffer[OffersManagementOfferDtoField.OfferSigners]
      .every((signer) => signer.isSigned);
  }

  static getIsUnderAccepting(offers) {
    const lastOffer = OfferManagementOfferDetailsService.getLastAcceptedOffer(offers);

    if (!lastOffer) {
      return false;
    }

    const signers = lastOffer[OffersManagementOfferDtoField.OfferSigners];

    return signers.some((signer) => signer.isSigned);
  }

  static getIsAllParticipantsAccepted(offers) {
    const lastAcceptedOffer = this.getLastAcceptedOffer(offers);

    if (!lastAcceptedOffer) {
      return false;
    }

    const signers = lastAcceptedOffer[OffersManagementOfferDtoField.OfferSigners];

    return signers.every((signer) => signer.isSigned);
  }

  static getIsRejected(offers) {
    return offers.some((offer) => (
      offer[OffersManagementOfferDtoField.OfferStatus] === OfferStatus.Rejected
    ));
  }

  static getPendingOffers(offers) {
    return offers.filter((offer) => (
      offer[OffersManagementOfferDtoField.OfferStatus] === OfferStatus.Pending
    ));
  }

  static getIsSubmitted(offers) {
    return !!this.getPendingOffers(offers).length;
  }

  static getIsExpired(offer) {
    const expireDate = offer[OffersManagementOfferDtoField.OfferExpirationDate];

    if (!expireDate) {
      return false;
    }

    return DateHelper.getIsPast(expireDate);
  }

  static getIsPreparingStatus(status) {
    return PREPARING_STEPPER_STATUSES.includes(status);
  }

  static getIsInitialForOpponentStatus(status) {
    return INITIAL_FOR_OPPONENT_STEPPER_STATUSES.includes(status);
  }

  static getHasAllNecessaryParticipants(offers, status) {
    if (this.getIsInitialForOpponentStatus(status) || this.getIsPreparingStatus(status)) {
      return true;
    }

    const hasBuyer = offers.some((offer) => (
      offer[OffersManagementUserDtoField.UserRole] === UserRole.Buyer
    ));
    const hasSeller = offers.some((offer) => (
      offer[OffersManagementUserDtoField.UserRole] === UserRole.Seller
    ));

    return hasSeller && hasBuyer;
  }

  static getIsMls(listing) {
    return listing[OffersManagementListingDtoField.ListingOrigin] !== Enums.ListingOriginEnum.BODE;
  }
}

export default OfferManagementOfferDetailsService;
